// ConsentForm.js - No auto-fill, no OTP display

import React, { useState, useEffect, useRef } from 'react';
import './ConsentForm.css';

// API Configuration
const API_URL = process.env.REACT_APP_API_PATH || 'http://localhost:8060/api/';

const ConsentForm = () => {
  // Get shopId from URL parameters
  const [shopId, setShopId] = useState('');
  const [formData, setFormData] = useState({
    shopName: '',
    ownerName: '',
    email: '',
    phone: '',
    shopAddress: '',
    agreeToTerms: false,
    agreeToDataProcessing: false,
    agreeToMarketing: false,
    agreeToLiability: false,
  });

  // OTP states
  const [otpSent, setOtpSent] = useState(false);
  const [enteredOtp, setEnteredOtp] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [timer, setTimer] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [submissionData, setSubmissionData] = useState(null);
  const [otpSuccessMessage, setOtpSuccessMessage] = useState('');

  const otpInputRef = useRef(null);
  const emailInputRef = useRef(null);

  // Extract shopId from URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get('shopId');
    if (id) {
      setShopId(id);
    }
  }, []);

  // Timer for OTP resend
  useEffect(() => {
    let countdown;
    if (timer > 0) {
      countdown = setTimeout(() => setTimer(timer - 1), 1000);
    }
    return () => {
      if (countdown) clearTimeout(countdown);
    };
  }, [timer]);

  // Check if email is already verified (for resume functionality)
  useEffect(() => {
    const checkVerification = async () => {
      if (formData.email && /\S+@\S+\.\S+/.test(formData.email)) {
        try {
          const response = await fetch(`${API_URL}Consent/check-verification?email=${encodeURIComponent(formData.email)}`);
          const data = await response.json();
          if (data.success && data.verified) {
            setOtpVerified(true);
            setOtpSent(true);
          }
        } catch (error) {
          console.error('Error checking verification:', error);
        }
      }
    };
    
    const timeoutId = setTimeout(checkVerification, 800);
    return () => clearTimeout(timeoutId);
  }, [formData.email]);

  // Auto-focus OTP input when OTP is sent
  useEffect(() => {
    if (otpSent && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [otpSent]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  // Send OTP to email
  const sendOTP = async () => {
    // Validate email first
    if (!formData.email.trim()) {
      setErrors({ ...errors, email: 'Email is required for OTP verification' });
      emailInputRef.current?.focus();
      return;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setErrors({ ...errors, email: 'Please enter a valid email address' });
      emailInputRef.current?.focus();
      return;
    }

    if (!shopId) {
      setOtpError('Shop ID is missing. Please refresh the page.');
      return;
    }

    setIsLoading(true);
    setOtpError('');
    setOtpSuccessMessage('');

    try {
      const response = await fetch(`${API_URL}Consent/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email.trim(),
          shopId: parseInt(shopId, 10),
          shopName: formData.shopName.trim(),
          ownerName: formData.ownerName.trim()
        }),
      });

      const data = await response.json();

      if (data.success) {
        setOtpSent(true);
        setTimer(60);
        setOtpError('');
        setOtpSuccessMessage(`✅ OTP sent to ${formData.email}`);
        // Clear any previous OTP input
        setEnteredOtp('');
      } else {
        setOtpError(data.message || 'Failed to send OTP. Please try again.');
      }
    } catch (error) {
      console.error('Send OTP error:', error);
      setOtpError('Failed to send OTP. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  // Verify OTP
  const verifyOTP = async () => {
    const otpValue = enteredOtp.trim();
    
    if (!otpValue) {
      setOtpError('Please enter the OTP');
      otpInputRef.current?.focus();
      return;
    }

    if (otpValue.length !== 6) {
      setOtpError('Please enter a valid 6-digit OTP');
      otpInputRef.current?.focus();
      return;
    }

    if (!/^\d{6}$/.test(otpValue)) {
      setOtpError('OTP must contain only numbers');
      otpInputRef.current?.focus();
      return;
    }

    setIsLoading(true);
    setOtpError('');
    setOtpSuccessMessage('');

    try {
      const response = await fetch(`${API_URL}Consent/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email.trim(),
          otp: otpValue
        }),
      });

      const data = await response.json();

      if (data.success) {
        setOtpVerified(true);
        setOtpError('');
        setOtpSuccessMessage('✅ Email verified successfully!');
        setEnteredOtp('');
        // Show success message
        alert('✅ Email verified successfully!');
      } else {
        setOtpError(data.message || 'Invalid OTP. Please try again.');
        setEnteredOtp('');
        otpInputRef.current?.focus();
      }
    } catch (error) {
      console.error('Verify OTP error:', error);
      setOtpError('Error verifying OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const resendOTP = async () => {
    if (timer > 0) {
      setOtpError(`Please wait ${timer} seconds before resending`);
      return;
    }

    setIsLoading(true);
    setOtpError('');
    setOtpSuccessMessage('');

    try {
      const response = await fetch(`${API_URL}Consent/resend-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email.trim(),
        }),
      });

      const data = await response.json();

      if (data.success) {
        setTimer(60);
        setOtpError('');
        setOtpSuccessMessage('✅ OTP resent successfully');
        setEnteredOtp('');
        alert('✅ OTP resent successfully');
      } else {
        setOtpError(data.message || 'Failed to resend OTP');
      }
    } catch (error) {
      console.error('Resend OTP error:', error);
      setOtpError('Failed to resend OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.shopName.trim()) {
      newErrors.shopName = 'Shop name is required';
    }
    if (!formData.ownerName.trim()) {
      newErrors.ownerName = 'Owner name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    if (!formData.shopAddress.trim()) {
      newErrors.shopAddress = 'Shop address is required';
    }
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }
    if (!formData.agreeToLiability) {
      newErrors.agreeToLiability = 'You must agree to the liability clause';
    }
    if (!otpVerified) {
      newErrors.otp = 'Please verify your email with OTP before submitting';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      const firstError = document.querySelector('.error-message');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsLoading(true);

    try {
      const submissionData = {
        ShopName: formData.shopName.trim(),
        OwnerName: formData.ownerName.trim(),
        Email: formData.email.trim(),
        Phone: formData.phone.trim(),
        ShopAddress: formData.shopAddress.trim(),
        ShopId: parseInt(shopId, 10),
        AgreeToTerms: formData.agreeToTerms,
        AgreeToDataProcessing: formData.agreeToDataProcessing,
        AgreeToMarketing: formData.agreeToMarketing,
        AgreeToLiability: formData.agreeToLiability
      };

      const response = await fetch(`${API_URL}Consent/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      const data = await response.json();

      if (data.success) {
        setSubmissionData(data);
        setIsSubmitted(true);
        console.log('✅ Consent submitted:', data);
      } else {
        alert(data.message || 'Failed to submit consent');
      }
    } catch (error) {
      console.error('Submit error:', error);
      alert('Error submitting consent. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      shopName: '',
      ownerName: '',
      email: '',
      phone: '',
      shopAddress: '',
      agreeToTerms: false,
      agreeToDataProcessing: false,
      agreeToMarketing: false,
      agreeToLiability: false,
    });
    setOtpSent(false);
    setOtpVerified(false);
    setEnteredOtp('');
    setOtpError('');
    setOtpSuccessMessage('');
    setErrors({});
    setTimer(0);
  };

  // Handle Enter key for OTP
  const handleOtpKeyPress = (e) => {
    if (e.key === 'Enter') {
      verifyOTP();
    }
  };

  // Success Component
  if (isSubmitted) {
    return (
      <div className="success-message">
        <div className="success-icon">✅</div>
        <h2>Consent Submitted Successfully!</h2>
        <div className="submission-details">
          <div className="detail-item">
            <strong>Consent ID:</strong>
            <span className="consent-id">{submissionData?.consentId}</span>
          </div>
          <div className="detail-item">
            <strong>Shop ID:</strong>
            <span>{submissionData?.shopId}</span>
          </div>
          <div className="detail-item">
            <strong>Email:</strong>
            <span>{submissionData?.email}</span>
          </div>
          <div className="detail-item">
            <strong>Timestamp:</strong>
            <span>{submissionData?.timestamp ? new Date(submissionData.timestamp).toLocaleString() : new Date().toLocaleString()}</span>
          </div>
          {submissionData?.verificationHash && (
            <div className="detail-item verification-hash">
              <strong>Verification Hash:</strong>
              <span className="hash">{submissionData.verificationHash}</span>
            </div>
          )}
        </div>
        <p className="success-message-text">
          Thank you for registering with LaundryxMart. We will review your application 
          and get back to you within 24-48 hours.
        </p>
        <p className="email-confirmation">
          📧 A confirmation email has been sent to <strong>{formData.email}</strong>
        </p>
        <button 
          onClick={() => {
            setIsSubmitted(false);
            handleReset();
          }} 
          className="btn-submit-another"
        >
          Submit Another Consent
        </button>
      </div>
    );
  }

  // Main Form
  return (
    <div className="consent-form-container">
      <div className="form-header">
        <h1>📋 LaundryxMart Partner Consent Form</h1>
        <p>Please fill out this form to give your consent for live shop on our marketplace</p>
        {shopId && (
          <div className="shop-id-badge">
            🏪 Shop ID: <strong>{shopId}</strong>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="consent-form">
        <div className="form-section">
          <h3>🏪 Shop Information</h3>
          
          <div className="form-group">
            <label htmlFor="shopName">Shop Name *</label>
            <input
              type="text"
              id="shopName"
              name="shopName"
              value={formData.shopName}
              onChange={handleChange}
              placeholder="Enter your laundry shop name"
              className={errors.shopName ? 'error' : ''}
              disabled={isLoading}
            />
            {errors.shopName && <span className="error-message">{errors.shopName}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="ownerName">Owner/Manager Full Name *</label>
            <input
              type="text"
              id="ownerName"
              name="ownerName"
              value={formData.ownerName}
              onChange={handleChange}
              placeholder="Enter full name as per legal documents"
              className={errors.ownerName ? 'error' : ''}
              disabled={isLoading}
            />
            {errors.ownerName && <span className="error-message">{errors.ownerName}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                ref={emailInputRef}
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                className={errors.email ? 'error' : ''}
                disabled={otpVerified || isLoading}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="1234567890"
                className={errors.phone ? 'error' : ''}
                disabled={isLoading}
                maxLength="10"
              />
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="shopAddress">Shop Address *</label>
            <textarea
              id="shopAddress"
              name="shopAddress"
              value={formData.shopAddress}
              onChange={handleChange}
              placeholder="Enter complete shop address"
              rows="3"
              className={errors.shopAddress ? 'error' : ''}
              disabled={isLoading}
            />
            {errors.shopAddress && <span className="error-message">{errors.shopAddress}</span>}
          </div>
        </div>

        {/* OTP Verification Section */}
        <div className="form-section otp-section">
          <h3>📧 Email Verification</h3>
          <p className="otp-instruction">
            Verify your email address with OTP to complete the consent process.
          </p>

          {!otpVerified ? (
            <div className="otp-container">
              {!otpSent ? (
                <div className="otp-send">
                  <button 
                    type="button" 
                    onClick={sendOTP} 
                    className="send-otp-btn"
                    disabled={isLoading || !formData.email}
                  >
                    {isLoading ? '⏳ Sending...' : '📧 Send OTP'}
                  </button>
                  <small className="otp-hint">
                    {formData.email 
                      ? `An OTP will be sent to ${formData.email}` 
                      : 'Please enter your email first'}
                  </small>
                </div>
              ) : (
                <div className="otp-verify">
                  <div className="otp-input-group">
                    <input
                      ref={otpInputRef}
                      type="text"
                      maxLength="6"
                      placeholder="Enter 6-digit OTP"
                      value={enteredOtp}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        setEnteredOtp(value);
                        setOtpError('');
                        setOtpSuccessMessage('');
                      }}
                      onKeyPress={handleOtpKeyPress}
                      className={otpError ? 'error' : ''}
                      disabled={isLoading}
                    />
                    <button 
                      type="button" 
                      onClick={verifyOTP} 
                      className="verify-otp-btn"
                      disabled={isLoading || !enteredOtp || enteredOtp.length !== 6}
                    >
                      {isLoading ? '⏳ Verifying...' : '✅ Verify'}
                    </button>
                  </div>
                  
                  <div className="otp-actions">
                    <button 
                      type="button" 
                      onClick={resendOTP} 
                      className="resend-otp-btn"
                      disabled={timer > 0 || isLoading}
                    >
                      {timer > 0 ? `⏳ Resend in ${timer}s` : '🔄 Resend OTP'}
                    </button>
                  </div>
                  
                  {otpError && <span className="error-message">{otpError}</span>}
                  {otpSuccessMessage && <span className="success-message-text">{otpSuccessMessage}</span>}
                  
                  <div className="otp-info">
                    <small>✓ OTP sent to {formData.email}</small>
                    <small>📌 Check your spam folder if not received</small>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="otp-verified-badge">
              <span className="checkmark">✅</span>
              Email Verified Successfully!
              <span className="verified-email">{formData.email}</span>
            </div>
          )}
          
          {errors.otp && <span className="error-message">{errors.otp}</span>}
        </div>

        <div className="form-section">
          <h3>✅ Terms & Consent</h3>

          <div className="consent-item">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={handleChange}
                disabled={isLoading}
              />
              <span className="checkbox-text">
                I agree to the <a href="/terms" target="_blank">Terms and Conditions</a> of LaundryxMart marketplace.
                I understand that I must maintain quality standards and fulfill orders promptly.
              </span>
            </label>
            {errors.agreeToTerms && <span className="error-message">{errors.agreeToTerms}</span>}
          </div>

          <div className="consent-item">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="agreeToDataProcessing"
                checked={formData.agreeToDataProcessing}
                onChange={handleChange}
                disabled={isLoading}
              />
              <span className="checkbox-text">
                I consent to the collection and processing of my business data as per the 
                <a href="/privacy" target="_blank"> Privacy Policy</a>.
              </span>
            </label>
          </div>

          <div className="consent-item">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="agreeToMarketing"
                checked={formData.agreeToMarketing}
                onChange={handleChange}
                disabled={isLoading}
              />
              <span className="checkbox-text">
                I consent to receive promotional communications from LaundryxMart (Optional).
              </span>
            </label>
          </div>

          <div className="consent-item">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="agreeToLiability"
                checked={formData.agreeToLiability}
                onChange={handleChange}
                disabled={isLoading}
              />
              <span className="checkbox-text">
                I agree to the <a href="/liability" target="_blank">Liability Clause</a>. I am responsible for service quality and any damages.
              </span>
            </label>
            {errors.agreeToLiability && <span className="error-message">{errors.agreeToLiability}</span>}
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="submit" 
            className="submit-btn"
            disabled={!otpVerified || isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span>
                Submitting...
              </>
            ) : (
              !otpVerified ? '🔒 Verify Email First' : '✅ Submit Consent'
            )}
          </button>
          <button 
            type="reset" 
            className="reset-btn"
            onClick={handleReset}
            disabled={isLoading}
          >
            🔄 Reset Form
          </button>
        </div>

        <div className="form-footer">
          <p>* Required fields</p>
          <p>Email verification is required before submitting this consent form.</p>
          {shopId && <p className="shop-id-footer">Reference Shop ID: {shopId}</p>}
        </div>
      </form>
    </div>
  );
};

export default ConsentForm;